import React, { useEffect, useState } from "react";
import { Modal, ProgressBar, Row, Col } from "react-bootstrap";
import config from "../../config/config";
import axios from "axios";
import { getAuthToken } from "../../utils/token";

const MonthlyProductionSummaryModal = ({ show, handleClose }) => {
    const [month_summary, setMonth_summary] = useState([]);

    const fetchProduction_summary = async () => {
        const token = getAuthToken();

        if (!token) return;

        try {
            const response = await axios.get(
                `${config.API}/manager/productionsummary`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response?.data?.success) {
                setMonth_summary(response.data || {});
            } else {
                setMonth_summary({});
            }
        } catch (err) {
            console.error(
                "Error fetching production summary:",
                err.message
            );
        }
    };

    useEffect(() => {
        if (show) {
            fetchProduction_summary();
        }
    }, [show]);

    const formatNumber = (value) =>
        Number(value || 0).toLocaleString("en-IN");

    const getEfficiency = (product) => {
        const produced = Number(product?.total_produced || 0);
        const nc = Number(product?.total_nc || 0);
        const target = Number(product?.target || 0);

        if (!target) return 0;

        return Math.max(
            0,
            Math.min(100, ((produced - nc) / target) * 100)
        );
    };

    return (
        <>
            <style>{`
                .production-summary-modal {
                    max-width: 850px;
                    width: 95%;
                }

                .production-summary-modal .modal-content {
                    border: 0;
                    border-radius: 18px;
                    overflow: hidden;
                    box-shadow: 0 15px 45px rgba(0,0,0,.18);
                }

                .production-summary-modal .modal-body {
                    max-height: 75vh;
                    overflow-y: auto;
                    background: #f8fafc;
                }

                .summary-product-card {
                    background: #fff;
                    border: 1px solid #e8edf3;
                    border-radius: 14px;
                    padding: 18px;
                    margin-bottom: 14px;
                    animation: summaryFade .35s ease both;
                    transition: all .2s ease;
                }

                .summary-product-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 22px rgba(0,0,0,.07);
                }

                .summary-product-card:nth-child(2) {
                    animation-delay: .05s;
                }

                .summary-product-card:nth-child(3) {
                    animation-delay: .1s;
                }

                .summary-product-card:nth-child(4) {
                    animation-delay: .15s;
                }

                @keyframes summaryFade {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .summary-stat {
                    background: #f8fafc;
                    border: 1px solid #edf0f4;
                    border-radius: 10px;
                    padding: 10px 12px;
                    height: 100%;
                }

                .summary-stat-label {
                    font-size: 11px;
                    color: #8993a4;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: .3px;
                }

                .summary-stat-value {
                    font-size: 19px;
                    font-weight: 700;
                    color: #172033;
                    margin-top: 2px;
                }

                .summary-progress {
                    height: 8px;
                    border-radius: 20px;
                    background: #edf1f5;
                }

                .summary-progress .progress-bar {
                    border-radius: 20px;
                    transition: width .8s ease;
                }

                .summary-month {
                    background: #f1f5ff;
                    color: #3157a6;
                    border: 1px solid #dce6ff;
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                }
            `}</style>

            <Modal
                show={show}
                onHide={handleClose}
                centered
                dialogClassName="production-summary-modal"
                className="font_family"
            >
                <Modal.Header closeButton className="border-0 pb-2">
                    <div>
                        <h4 className="fw-bold mb-1">
                            Production Summary
                        </h4>

                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">
                                Monthly production performance
                            </span>

                            <span className="summary-month">
                                {month_summary?.month ||
                                    new Date().toLocaleString(
                                        "en-US",
                                        { month: "long" }
                                    )}
                            </span>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    {month_summary?.production_summary?.length > 0 ? (
                        month_summary.production_summary.map(
                            (product, idx) => {
                                const produced = Number(
                                    product?.total_produced || 0
                                );

                                const inspected = Number(
                                    product?.total_inspected || 0
                                );

                                const nc = Number(
                                    product?.total_nc || 0
                                );

                                const target = Number(
                                    product?.target || 0
                                );

                                const efficiency =
                                    getEfficiency(product);

                                return (
                                    <div
                                        key={
                                            product?.product_id ||
                                            idx
                                        }
                                        className="summary-product-card"
                                    >
                                        {/* Product Header */}
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="pe-3">
                                                <div className="text-muted small mb-1">
                                                    PRODUCT
                                                </div>

                                                <div className="fw-bold text-dark">
                                                    {product?.product
                                                        ?.name ||
                                                        "Unknown Product"}
                                                </div>
                                            </div>

                                            <div className="text-end">
                                                <div className="text-muted small">
                                                    PRODUCTION
                                                </div>

                                                <div
                                                    className="fw-bold"
                                                    style={{
                                                        color:
                                                            efficiency >=
                                                            90
                                                                ? "#198754"
                                                                : efficiency >=
                                                                  70
                                                                ? "#d39e00"
                                                                : "#dc3545",
                                                        fontSize:
                                                            "20px",
                                                    }}
                                                >
                                                    {efficiency.toFixed(
                                                        1
                                                    )}
                                                    %
                                                </div>
                                            </div>
                                        </div>

                                        {/* Progress */}
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="text-muted">
                                                Production Progress
                                            </span>

                                            <span className="fw-semibold">
                                                {formatNumber(
                                                    produced
                                                )}{" "}
                                                /{" "}
                                                {formatNumber(
                                                    target
                                                )}
                                            </span>
                                        </div>

                                        <ProgressBar
                                            now={Math.min(
                                                100,
                                                target
                                                    ? (produced /
                                                          target) *
                                                          100
                                                    : 0
                                            )}
                                            className="summary-progress mb-3"
                                        />

                                        {/* Statistics */}
                                        <Row className="g-2">
                                            <Col xs={4}>
                                                <div className="summary-stat">
                                                    <div className="summary-stat-label">
                                                        Produced
                                                    </div>

                                                    <div className="summary-stat-value">
                                                        {formatNumber(
                                                            produced
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col xs={4}>
                                                <div className="summary-stat">
                                                    <div className="summary-stat-label">
                                                        Inspected
                                                    </div>

                                                    <div className="summary-stat-value">
                                                        {formatNumber(
                                                            inspected
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col xs={4}>
                                                <div className="summary-stat">
                                                    <div className="summary-stat-label">
                                                        NC
                                                    </div>

                                                    <div
                                                        className="summary-stat-value"
                                                        style={{
                                                            color:
                                                                nc > 0
                                                                    ? "#dc3545"
                                                                    : "#198754",
                                                        }}
                                                    >
                                                        {formatNumber(
                                                            nc
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            }
                        )
                    ) : (
                        <div className="text-center py-5 text-muted">
                            <div className="fw-semibold">
                                No production summary available
                            </div>

                            <small>
                                There is no production data for
                                this month.
                            </small>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default MonthlyProductionSummaryModal;